/**
 * Whiteboard Component
 * File directory for uploading and managing documents
 */

import React, { useState, useRef, useEffect } from 'react';
import './Whiteboard.css';
import { Upload, FileText, Image, X, Folder } from 'lucide-react';

const Whiteboard = ({ isOpen, onClose, scenarioId, onArtifactsChange, onSaveUpload, savedArtifacts = [], tasks = [], scenarioTitles = {}, uploadError = null }) => {
  const [documents, setDocuments] = useState([]);
  const fileInputRef = useRef(null);
  const skipNotifyRef = useRef(false);

  // Seed from ALL saved artifacts (doc finder: show every uploaded file across all scenarios)
  useEffect(() => {
    skipNotifyRef.current = true;
    const fromSaved = (savedArtifacts || []).map(a => ({
      id: a.id || `saved-${a.scenarioId || ''}-${a.fileName}`,
      name: a.fileName,
      type: (a.fileType || '').startsWith('image/') ? 'image' : 'document',
      content: null,
      fileSize: a.fileSize ?? a.file_size ?? null,
      uploadedAt: a.uploadedAt || new Date().toISOString(),
      taskIndex: a.taskIndex ?? null,
      scenarioId: a.scenarioId ?? null,
      saved: true
    }));
    setDocuments(prev => {
      const localWithContent = prev.filter(d => d.content);
      const savedIds = new Set(fromSaved.map(d => d.id));
      const localOnly = localWithContent.filter(d => !savedIds.has(d.id));
      return [...fromSaved, ...localOnly];
    });
  }, [savedArtifacts]);

  // Notify parent only when user added/removed a doc (avoid loop: parent -> savedArtifacts -> effect 1 -> setDocuments -> effect 2 -> onArtifactsChange -> parent)
  useEffect(() => {
    if (skipNotifyRef.current) {
      skipNotifyRef.current = false;
      return;
    }
    if (onArtifactsChange) {
      const artifacts = documents.map(doc => ({
        id: doc.id,
        scenarioId: doc.scenarioId ?? scenarioId ?? null,
        taskIndex: doc.taskIndex ?? null,
        fileName: doc.name,
        fileType: doc.type,
        fileSize: doc.fileSize ?? null,
        uploadedAt: doc.uploadedAt
      }));
      onArtifactsChange(artifacts);
    }
  }, [documents, scenarioId, onArtifactsChange]);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        const newDoc = {
          id: `doc-${Date.now()}-${Math.random()}`,
          name: file.name,
          type: file.type.startsWith('image/') ? 'image' : 'document',
          content: dataUrl,
          fileSize: file.size,
          uploadedAt: new Date().toISOString(),
          taskIndex: null,
          scenarioId: scenarioId || null
        };
        setDocuments(prev => [...prev, newDoc]);
        if (onSaveUpload && scenarioId) {
          // Send metadata only (no fileData) to avoid 413 Payload Too Large; artifact row still saves for list/pill
          onSaveUpload({
            fileName: file.name,
            taskIndex: null,
            fileType: file.type,
            fileData: null,
            fileSize: file.size
          }).catch((err) => {
            console.error('[Whiteboard] Save upload failed:', err?.response?.data?.error || err?.message || err);
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const dataUrl = event.target.result;
          const newDoc = {
            id: `doc-${Date.now()}-${Math.random()}`,
            name: file.name,
            type: file.type.startsWith('image/') ? 'image' : 'document',
            content: dataUrl,
            fileSize: file.size,
            uploadedAt: new Date().toISOString(),
            taskIndex: null,
            scenarioId: scenarioId || null
          };
          setDocuments(prev => [...prev, newDoc]);
          if (onSaveUpload && scenarioId) {
            onSaveUpload({ fileName: file.name, taskIndex: null, fileType: file.type, fileData: null, fileSize: file.size }).catch((err) => {
              console.error('[Whiteboard] Save upload failed:', err?.response?.data?.error || err?.message || err);
            });
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeDocument = (id) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  };

  if (!isOpen) return null;

  return (
    <div className="whiteboard-overlay" onClick={onClose}>
      <div className="whiteboard-panel" onClick={(e) => e.stopPropagation()}>
        <div className="whiteboard-header">
          <div className="workspace-header-title">
            <Folder size={20} />
            <h2>Workspace</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="workspace-content">
          {uploadError && (
            <div className="workspace-save-error" role="alert">
              <strong>File not saved to account:</strong> {uploadError}
              <p className="workspace-save-error-hint">The file is only on this device until you fix this. Check the console (F12) for details.</p>
            </div>
          )}
          <div className="workspace-upload-section">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt,.md"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            
            <div
              className="workspace-upload-zone"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={32} />
              <p>Click to upload or drag and drop files</p>
              <p className="upload-hint">Supports images, PDFs, documents, and text files</p>
            </div>
          </div>

          {documents.length > 0 && (
            <div className="workspace-files-section">
              <h3>All uploads ({documents.length})</h3>
              <p className="workspace-doc-finder-hint">Saved files from all scenarios — like a doc finder.</p>
              <div className="files-list">
                {documents.map(doc => {
                  const sizeBytes = doc.fileSize ?? (doc.content ? Math.round((doc.content.length * 3) / 4) : null);
                  const fileSizeLabel = sizeBytes != null && sizeBytes > 0
                    ? (sizeBytes >= 1024 ? `${Math.round(sizeBytes / 1024)} KB` : `${sizeBytes} B`)
                    : '—';
                  const uploadDate = doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : 'Unknown';
                  const scenarioLabel = (doc.scenarioId && scenarioTitles[doc.scenarioId]) ? scenarioTitles[doc.scenarioId] : (doc.scenarioId || 'This scenario');
                  return (
                    <div key={doc.id} className="file-item">
                      <div className="file-icon">
                        {doc.type === 'image' ? (
                          <Image size={20} />
                        ) : (
                          <FileText size={20} />
                        )}
                      </div>
                      <div className="file-details">
                        <p className="file-name">{doc.name}</p>
                        <div className="file-meta">
                          <span className="file-size">{fileSizeLabel}</span>
                          <span className="file-separator">•</span>
                          <span className="file-date">{uploadDate}</span>
                          <span className="file-separator">•</span>
                          <span className="file-scenario" title={doc.scenarioId}>{scenarioLabel}</span>
                        </div>
                      </div>
                      <button
                        className="file-delete-button"
                        onClick={() => removeDocument(doc.id)}
                        title="Delete file"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {documents.length === 0 && (
            <div className="workspace-empty-state">
              <Folder size={48} />
              <p>No files uploaded yet</p>
              <p className="empty-hint">Upload documents, PRDs, user journey maps, or any other artifacts you create while working on tasks.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Whiteboard;







